// CTV-653: the Waymark generation panel, extracted verbatim from
// UploadCreativeStep.tsx so the wizard's creative step and the Edit
// commercial tab (EditCreativesTabPanel) share one implementation. The only
// behavioral change from the extraction: the react-hook-form coupling
// (useFormContext watch/setValue on businessWebsite) became the
// `businessWebsite` / `onBusinessWebsiteChange` prop pair, because the swap
// tab has no form context.
//
// CTV-775 "the ledger" (docs/design/ctv775-banner-options.html, option 1):
// the expanded body stopped repeating the banner. Division of labor is
// strict: the banner owns the stage word, the current estimate, the progress
// bar and the PRIMARY action pill (Review / Run this commercial / Make
// another commercial); this panel owns everything the banner cannot say: a
// timeline receipt of what happened (with observed timestamps), what is
// happening (a sentence, not a repeated stage word), what comes next (with
// the future estimates), the campaign's committed commercials as receipt
// rows, the video player, and the utility strip (source URL edit, Start
// over, the FAQ). The old in-body progress strip, spinner rows, and
// duplicated hero CTAs are gone.

import React from 'react';
import { Sparkles, AlertTriangle, Pencil, HelpCircle, X } from 'lucide-react';
import { tintFromAccent } from '../steps/_shared';
import { useWaymarkOptional } from './context';
import type { WaymarkStage } from './context';
import { looksLikeWebsiteUrl } from './normalizeUrl';
import { LedgerMarker, LedgerTimeline, formatClock, type LedgerRowSpec } from './ledger';
import { useAuth } from '../auth/useAuth';
import { apiClient, ApiError } from '../../../lib/apiClient';
import Banner from '../shared/Banner';
import PrimaryButton from '../shared/PrimaryButton';
import { useCreativePreviewUrl } from '../shared/useCreativePreviewUrl';
import { AdvertiserFaqList } from '../help/advertiserFaq';
import * as analytics from '../../../lib/analytics';
import { EVENTS } from '../../../lib/analytics';
import type { EditorSession, EditorSessionPhase, ReceiptEntry } from './types';

interface WaymarkPanelProps {
  accentColor: string;
  primaryColor?: string;
  // The station slug, for signed poster URLs of the receipt rows.
  slug: string;
  // True once the ACTIVE AI commercial has been committed to a slot on this
  // surface. Drives the "timeline" vs "campaign receipt" branches.
  waymarkCommitted: boolean;
  // CTV-785: true when at least one AI commercial exists on the surface and
  // every one has been in the campaign at some point (nothing is being made).
  // The panel then shows the campaign receipt; the banner pill is the only
  // Make another affordance.
  allSettled?: boolean;
  // CTV-785: the finished AI commercials on the surface (receipt.ts), in
  // creation order, each joined to the slot carrying it right now. A
  // commercial no slot carries shows "Not in your campaign" + Add to campaign.
  receipt: ReceiptEntry[];
  onAddToCampaign?: (commercialId: string) => void;
  addingCommercialId?: string | null;
  // CTV-785: open a committed commercial in a side editor session (the
  // surfaces' handleCustomizeSlot via useWaymarkEditorSession); the active
  // commercial is left alone. Wired to the receipt rows' Review links.
  onCustomizeCommitted?: (commercialId: string) => void;
  // CTV-785: the side editor session in progress, if any, so the receipt row
  // it belongs to shows where it is and the other rows wait their turn.
  reviewSession?: EditorSession | null;
  // CTV-785: the side session's last failure (open, download, ad specs).
  reviewError?: string | null;
  // 1-based ordinal of the active commercial on the surface (for the
  // "Commercial N" section label when committed rows sit above the timeline).
  activeOrdinal?: number;
  // CTV-775: the station's display name, for the AI commercial FAQ copy.
  publisherName: string;
  // CTV-563: composed human message when the last Waymark render failed the
  // ad-spec check. Null when there's no active rejection. Clearing it lets
  // the buyer try again.
  rejection?: string | null;
  onClearRejection?: () => void;
  // CTV-653: the advertiser's website URL, hoisted by the parent surface
  // (form state in the wizard, local state in the swap tab).
  businessWebsite: string;
  onBusinessWebsiteChange: (next: string) => void;
}

const WaymarkPanel: React.FC<WaymarkPanelProps> = ({
  accentColor,
  primaryColor,
  slug,
  waymarkCommitted,
  allSettled = false,
  receipt,
  onAddToCampaign,
  addingCommercialId = null,
  onCustomizeCommitted,
  reviewSession = null,
  reviewError = null,
  activeOrdinal,
  publisherName,
  rejection = null,
  onClearRejection,
  businessWebsite: businessWebsiteProp,
  onBusinessWebsiteChange,
}) => {
  const businessWebsite = businessWebsiteProp.trim();
  // CTV-775: null when no AI commercial is active on the surface (every one is
  // committed, or none exists yet). Only the campaign receipt renders then.
  const waymark = useWaymarkOptional();
  const status = waymark?.status ?? 'idle';
  const sourceUrl = waymark?.sourceUrl ?? null;
  const renderedUrl = waymark?.renderedUrl ?? null;
  const isHydrating = waymark?.isHydrating ?? false;
  const isFinalizing = waymark?.isFinalizing ?? false;
  const partnerUnavailable = waymark?.partnerUnavailable ?? false;
  const stageEnteredAt = waymark?.stageEnteredAt ?? {};
  const activeCommercialId = waymark?.commercialId ?? null;
  const openEditor = React.useCallback(() => waymark?.openEditor() ?? Promise.resolve(), [waymark]);
  const startGenerationForUrl = React.useCallback(
    (url: string) => waymark?.startGenerationForUrl(url),
    [waymark],
  );
  const restartGeneration = React.useCallback(() => waymark?.restartGeneration(), [waymark]);
  // CTV-562 follow-up: the Waymark source-URL editor is a second surface
  // that mutates business identity. Route the change through the seat's
  // PATCH /advertisers/:id + refreshMemberships so the read-only summary
  // on Step 1 reflects the new URL (single source of truth). Without this,
  // the multi-seat dropdown summary reads selected.businessWebsite from
  // the stale memberships list and Step 1 keeps showing the old URL.
  const { selectedAdvertiser, refreshMemberships } = useAuth();

  // CTV-558: gate URL swaps during in-flight generation behind a confirm
  // modal. `pendingUrl` is the candidate URL the user just typed; the
  // resolver is the promise hook that WaymarkSourceWebsiteRow is awaiting,
  // so we can answer it after the modal closes.
  const [confirmTarget, setConfirmTarget] = React.useState<{
    url: string;
    resolve: (ok: boolean) => void;
  } | null>(null);

  // CTV-564: latch true once isFinalizing has fired in this generation
  // cycle. Without this, the ledger reverts to "Make it yours" after the
  // broadcast render finishes (because isFinalizing flips back to false
  // and status stays 'ready'). The provider doesn't persist this; it's
  // an in-session signal, reset whenever a new generation begins.
  const [hasFinalized, setHasFinalized] = React.useState(false);
  React.useEffect(() => {
    if (isFinalizing) setHasFinalized(true);
  }, [isFinalizing]);
  React.useEffect(() => {
    if (status !== 'ready') setHasFinalized(false);
  }, [status]);

  // CTV-775 Start over: same restart confirm as the Edit website row, on the
  // commercial's current URL. Confirmed → restartGeneration() on the SAME
  // row; cancelled → nothing. Both outcomes are tracked.
  const [startOverOpen, setStartOverOpen] = React.useState(false);

  // CTV-562 follow-up: PATCH /advertisers/:id + refreshMemberships when the
  // Waymark source URL commit diverges from the seat's stored website. Throws
  // on failure so the inline SourceUrlEditor error surface (which now catches
  // onBeforeCommit throws) can display a message and keep the user in edit
  // mode. No-op when there's no seat (anonymous flow) or when the value
  // hasn't actually changed against the seat.
  const commitSeatWebsite = React.useCallback(
    async (next: string): Promise<void> => {
      if (!selectedAdvertiser) return;
      const trimmed = next.trim();
      const current = (selectedAdvertiser.businessWebsite ?? '').trim();
      if (trimmed === current) return;
      try {
        await apiClient.patch(`/advertisers/${selectedAdvertiser.advertiserId}`, {
          businessWebsite: trimmed || null,
        });
        await refreshMemberships();
      } catch (e) {
        throw e instanceof ApiError
          ? e
          : new Error(e instanceof Error ? e.message : 'Failed to update business URL.');
      }
    },
    [selectedAdvertiser, refreshMemberships],
  );

  const handleSourceCommit = React.useCallback(
    async (next: string): Promise<boolean> => {
      // Failed → swap and immediately re-attempt with the new URL; no
      // confirm needed since the prior run is already dead.
      if (status === 'topic_failed' || status === 'generation_failed') {
        await commitSeatWebsite(next);
        startGenerationForUrl(next);
        return true;
      }
      // Idle → just update the value. The "Build my commercial" button
      // remains the explicit start trigger.
      if (status === 'idle') {
        await commitSeatWebsite(next);
        return true;
      }
      // In-flight (topic_searching / generating / ready) → confirm before
      // tearing down the running generation.
      const ok = await new Promise<boolean>((resolve) => {
        setConfirmTarget({ url: next, resolve });
      });
      if (!ok) return false;
      await commitSeatWebsite(next);
      startGenerationForUrl(next);
      return true;
    },
    [status, startGenerationForUrl, commitSeatWebsite],
  );

  if (isHydrating) return null;

  // CTV-775 ledger: every AI commercial on the surface is committed (or the
  // active one is, at rest): the campaign receipt. One row per committed
  // commercial; Make another lives on the banner pill only.
  if (allSettled || (waymarkCommitted && !isFinalizing && status !== 'topic_searching' && status !== 'generating')) {
    // The card chrome lives in WaymarkDisclosure's attached shell.
    return (
      <div data-testid="waymark-campaign-receipt">
        {receipt.map((r) => (
          <ReceiptRow
            key={r.commercialId}
            slug={slug}
            entry={r}
            accentColor={accentColor}
            onCustomize={
              onCustomizeCommitted ? () => onCustomizeCommitted(r.commercialId) : undefined
            }
            onAdd={onAddToCampaign ? () => onAddToCampaign(r.commercialId) : undefined}
            adding={addingCommercialId === r.commercialId}
            reviewPhase={reviewSession?.commercialId === r.commercialId ? reviewSession.phase : null}
            reviewBusy={reviewSession != null}
          />
        ))}
        {reviewError && <ReviewError message={reviewError} />}
        {!allSettled && (
          <p className="text-[11.5px] text-slate-500 py-1" data-testid="waymark-receipt-note">
            Another commercial is in progress. It will show up here when it is ready.
          </p>
        )}
        <LedgerFooter
          accentColor={accentColor}
          primaryColor={primaryColor}
          publisherName={publisherName}
          sourceValue={businessWebsite || (sourceUrl ?? '')}
          onSourceChange={onBusinessWebsiteChange}
          onBeforeSourceCommit={handleSourceCommit}
        />
      </div>
    );
  }

  // CTV-643: the draft's Waymark account is under a partner we can no longer
  // authenticate as (the publisher's agreement was cleared or repointed after
  // the commercial was started). The editor can't be reopened and regenerating
  // would hit the same wall, so the only way forward is uploading a file. This
  // takes priority over every other panel state. Mirrors the rework flow's
  // "can't be reopened" dead-end (EditCreativesTabPanel).
  if (partnerUnavailable) {
    return (
      <Banner
        tone="warn"
        label="This commercial can't be reopened"
        icon={<AlertTriangle className="w-3 h-3" />}
      >
        <p>
          The AI commercial editor isn't available for this campaign anymore.
          Upload your own video above to continue.
        </p>
      </Banner>
    );
  }

  // CTV-563: rejection banner sits above every other panel state: once a
  // Waymark render has been flagged as out-of-spec we hold this callout in
  // front of the buyer until they either retry generation or edit the
  // commercial. Try again clears the rejection and re-kicks Waymark.
  if (rejection) {
    return (
      <Banner
        tone="warn"
        label="This commercial doesn't meet the ad specs"
        icon={<AlertTriangle className="w-3 h-3" />}
      >
        <p>{rejection}</p>
        <button
          type="button"
          onClick={() => {
            onClearRejection?.();
            if (businessWebsite) startGenerationForUrl(businessWebsite);
          }}
          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-rose-900 hover:text-rose-950 underline underline-offset-2"
        >
          Try again
        </button>
      </Banner>
    );
  }

  if (!businessWebsite && status === 'idle') {
    return (
      <WaymarkWebsiteCapture
        accentColor={accentColor}
        primaryColor={primaryColor}
        onCommit={(url) => {
          onBusinessWebsiteChange(url);
          startGenerationForUrl(url);
        }}
      />
    );
  }

  if (status === 'topic_failed' || status === 'generation_failed') {
    return (
      <Banner
        tone="warn"
        label="We couldn't build your commercial"
        icon={<AlertTriangle className="w-3 h-3" />}
      >
        <p>
          Try a different website, or upload your own video above.
        </p>
        <WaymarkSourceWebsiteRow
          accentColor={accentColor}
          primaryColor={primaryColor}
          value={businessWebsite || (sourceUrl ?? '')}
          onChange={(next) => onBusinessWebsiteChange(next)}
          onBeforeCommit={handleSourceCommit}
        />
        <button
          type="button"
          onClick={() => businessWebsite && startGenerationForUrl(businessWebsite)}
          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-rose-900 hover:text-rose-950 underline underline-offset-2"
        >
          Try again
        </button>
      </Banner>
    );
  }

  // The ledger: what happened (checked rows with observed times), what is
  // happening (one explained row), what comes next (dim rows with the
  // estimates the banner never shows). Ready collapses the history to a
  // one-line summary above the player.
  if (
    status === 'topic_searching' ||
    status === 'generating' ||
    status === 'ready'
  ) {
    // CTV-775: a persisted broadcast render (refresh after Finish, or a
    // resumed commercial) is ready to run without another editor pass.
    const allComplete = status === 'ready' && !isFinalizing && (hasFinalized || !!renderedUrl);
    const stage: WaymarkStage = allComplete
      ? 'ready'
      : status === 'topic_searching'
        ? 'analyzing'
        : status === 'generating'
          ? 'drafting'
          : isFinalizing
            ? 'finalizing'
            : 'customize';

    // Finished commercials compress to receipt rows above the timeline; the
    // active commercial is excluded there (it IS the timeline). Ordinals are
    // creation order, so rows keep their names across states.
    const receiptRows = receipt.filter((r) => r.commercialId !== activeCommercialId);
    const sectionOrdinal =
      activeOrdinal && activeOrdinal > 0 ? activeOrdinal : receipt.length + 1;
    const host = displayHost(businessWebsite || sourceUrl || '');

    // The card chrome lives in WaymarkDisclosure's attached shell.
    return (
      <>
        <div data-testid="waymark-ledger">
          {receiptRows.length > 0 && (
            <>
              {receiptRows.map((r) => (
                <ReceiptRow
                  key={r.commercialId}
                  slug={slug}
                  entry={r}
                  accentColor={accentColor}
                  onCustomize={
                    onCustomizeCommitted ? () => onCustomizeCommitted(r.commercialId) : undefined
                  }
                  onAdd={onAddToCampaign ? () => onAddToCampaign(r.commercialId) : undefined}
                  adding={addingCommercialId === r.commercialId}
                  reviewPhase={reviewSession?.commercialId === r.commercialId ? reviewSession.phase : null}
                  reviewBusy={reviewSession != null}
                />
              ))}
              {reviewError && <ReviewError message={reviewError} />}
              <div className="h-px bg-slate-100 my-2.5" aria-hidden />
              <div
                className="text-[10px] font-bold uppercase tracking-[0.06em] text-slate-400 mb-1"
                data-testid="waymark-ledger-section"
              >
                Commercial {sectionOrdinal}
                {stage !== 'ready' && ' · on its way'}
              </div>
            </>
          )}

          {stage === 'ready' ? (
            <LedgerReadyBody
              accentColor={accentColor}
              host={host}
              renderedUrl={renderedUrl}
              stageEnteredAt={stageEnteredAt}
            />
          ) : (
            <LedgerTimeline
              accentColor={accentColor}
              rows={LEDGER_ROWS}
              stage={stage}
              host={host}
              priorCount={receiptRows.length}
              stageEnteredAt={stageEnteredAt}
            />
          )}

          <LedgerFooter
            accentColor={accentColor}
            primaryColor={primaryColor}
            publisherName={publisherName}
            sourceValue={businessWebsite || (sourceUrl ?? '')}
            onSourceChange={onBusinessWebsiteChange}
            onBeforeSourceCommit={handleSourceCommit}
            // Review demotes to a utility link once the video is done
            // (the banner pill is Run this commercial there); in the
            // customize stage the banner pill IS Review, so no link.
            onCustomize={stage === 'ready' ? () => void openEditor() : undefined}
            onStartOver={waymarkCommitted ? undefined : () => setStartOverOpen(true)}
          />
        </div>

        {startOverOpen && (
          <RestartGenerationConfirm
            accentColor={accentColor}
            primaryColor={primaryColor}
            title="Start over?"
            confirmLabel="Start over"
            body="discard"
            newUrl={businessWebsite || sourceUrl || ''}
            onCancel={() => {
              analytics.track(EVENTS.WAYMARK_START_OVER_CLICKED, { confirmed: false });
              setStartOverOpen(false);
            }}
            onConfirm={() => {
              analytics.track(EVENTS.WAYMARK_START_OVER_CLICKED, { confirmed: true });
              setStartOverOpen(false);
              restartGeneration();
            }}
          />
        )}

        {confirmTarget && (
          <RestartGenerationConfirm
            accentColor={accentColor}
            primaryColor={primaryColor}
            newUrl={confirmTarget.url}
            onCancel={() => {
              confirmTarget.resolve(false);
              setConfirmTarget(null);
            }}
            onConfirm={() => {
              confirmTarget.resolve(true);
              setConfirmTarget(null);
            }}
          />
        )}
      </>
    );
  }

  // Idle but website-on-file: pre-generation "Build my commercial" content.
  // Borderless: WaymarkDisclosure's attached shell is the card.
  return (
    <div data-testid="waymark-idle-card">
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: tintFromAccent(accentColor, 0.14), color: accentColor }}
        >
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-1"
            style={{ color: accentColor }}
          >
            Don't have a commercial?
          </div>
          <div className="text-[15px] font-semibold tracking-tight text-slate-900">
            Build one from your website
          </div>
          <WaymarkSourceWebsiteRow
            accentColor={accentColor}
            primaryColor={primaryColor}
            value={businessWebsite}
            onChange={(next) => onBusinessWebsiteChange(next)}
            onBeforeCommit={handleSourceCommit}
          />
          <p className="text-[12px] text-slate-500 leading-relaxed mt-2">
            We'll draft a free 30-second commercial you can edit. Takes about five minutes total.
          </p>
          <div className="mt-3">
            <PrimaryButton
              type="button"
              onClick={() => startGenerationForUrl(businessWebsite)}
              accentColor={accentColor}
              primaryColor={primaryColor}
              size="sm"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Build my commercial
            </PrimaryButton>
          </div>
        </div>
      </div>
      <HowDoesThisWork accentColor={accentColor} publisherName={publisherName} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// CTV-775 ledger pieces
// ---------------------------------------------------------------------------

/** "https://www.acme.com/about" → "www.acme.com" (buyer-recognizable host). */
function displayHost(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
}

const LEDGER_ROWS: LedgerRowSpec[] = [
  {
    key: 'analyzing',
    doneLabel: (host) => `Analyzed ${host}`,
    completedAtStage: 'drafting',
    currentTitle: (host) => `Reading ${host}`,
    currentDesc: () => 'Grabbing your logo, brand colors, photos and copy to build from.',
    currentHint: '≈8 min total',
    todoLabel: 'Analyzing your website',
    todoHint: 'about 30s',
  },
  {
    key: 'drafting',
    doneLabel: () => 'Draft completed',
    completedAtStage: 'customize',
    currentTitle: () => 'Creating your 30-second commercial',
    // CTV-785: the title carries the whole message; only the make-another
    // case keeps a subtext (the reassurance that committed ones are safe).
    currentDesc: (_host, priorCount) =>
      priorCount > 0
        ? `A fresh script and scenes from the same website. Your ${priorCount === 1 ? 'first commercial is' : 'other commercials are'} untouched.`
        : null,
    currentHint: '≈7 min total',
    todoLabel: 'Creating your 30-second commercial',
    todoHint: 'about 4 min',
  },
  {
    key: 'customize',
    doneLabel: () => 'You customized it',
    completedAtStage: 'finalizing',
    currentTitle: () => 'Make it yours',
    currentDesc: () =>
      'Open the editor to review and customize your ad.',
    currentHint: 'waiting on you',
    todoLabel: 'You customize the words and pictures',
    todoHint: 'your pace',
  },
  {
    key: 'finalizing',
    doneLabel: () => 'Commercial uploaded',
    completedAtStage: 'ready',
    currentTitle: () => 'Uploading your commercial',
    currentDesc: () => 'We are getting your commercial ready for your campaign.',
    currentHint: null,
    todoLabel: 'Uploading your commercial',
    todoHint: 'about 3 min',
  },
];

// Ready: the history collapses to one line ("Made in 7 minutes" when the
// whole run was observed in this session, else just the source), then the
// player, then the offer chips. Run lives on the banner pill only.
const LedgerReadyBody: React.FC<{
  accentColor: string;
  host: string;
  renderedUrl: string | null;
  stageEnteredAt: Partial<Record<WaymarkStage, number>>;
}> = ({ accentColor, host, renderedUrl, stageEnteredAt }) => {
  const startedAt = stageEnteredAt.analyzing;
  const readyAt = stageEnteredAt.ready;
  const madeInMin =
    startedAt != null && readyAt != null && readyAt > startedAt
      ? Math.max(1, Math.round((readyAt - startedAt) / 60000))
      : null;
  const readyTime = formatClock(readyAt);
  return (
    <div>
      <div className="flex items-start gap-2.5 py-1" data-testid="waymark-ready-summary">
        <LedgerMarker kind="done" accentColor={accentColor} />
        <span className="flex-1 min-w-0 text-[12.5px] font-semibold text-slate-900">
          {madeInMin != null
            ? `Made in ${madeInMin} minute${madeInMin === 1 ? '' : 's'} from ${host}`
            : `Made from ${host}`}
        </span>
        {readyTime && (
          <span className="ml-auto pl-2.5 pt-0.5 text-[10.5px] text-slate-400 tabular-nums whitespace-nowrap">
            {readyTime}
          </span>
        )}
      </div>
      {renderedUrl ? (
        <video
          src={renderedUrl}
          controls
          className="w-full rounded-xl border border-slate-200/70 bg-black mt-1.5"
        />
      ) : (
        <p className="text-[11.5px] text-slate-500 mt-1.5">Loading your video…</p>
      )}
      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="waymark-ready-chips">
        <span
          className="text-[10.5px] font-semibold rounded-full px-2 py-0.5"
          style={{
            color: accentColor,
            backgroundColor: tintFromAccent(accentColor, 0.1),
            border: `1px solid ${tintFromAccent(accentColor, 0.25)}`,
          }}
        >
          Free
        </span>
        <span className="text-[10.5px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
          0:30
        </span>
      </div>
    </div>
  );
};

// One committed commercial, one line: mini poster, positional name, and the
// Review link that opens it in a side editor session (the surfaces'
// handleCustomizeSlot). CTV-785: while a session is live, its row says where
// it is and every row's Review waits (one editor at a time).
const REVIEW_PHASE_COPY: Record<EditorSessionPhase, string> = {
  opening: 'Opening the editor',
  editing: 'Open in the editor',
  uploading: 'Uploading your changes',
};

const ReceiptRow: React.FC<{
  slug: string;
  entry: ReceiptEntry;
  accentColor: string;
  onCustomize?: () => void;
  onAdd?: () => void;
  adding?: boolean;
  reviewPhase?: EditorSessionPhase | null;
  reviewBusy?: boolean;
}> = ({
  slug,
  entry,
  accentColor,
  onCustomize,
  onAdd,
  adding = false,
  reviewPhase = null,
  reviewBusy = false,
}) => {
  const { url, posterUrl } = useCreativePreviewUrl(slug, entry.storagePath);
  // CTV-785: a finished commercial no slot carries previews from its render.
  const videoSrc = url ?? entry.renderedUrl;
  const statusCopy = reviewPhase
    ? REVIEW_PHASE_COPY[reviewPhase]
    : adding
      ? 'Adding to your campaign'
      : entry.inCampaign
        ? 'In your campaign'
        : 'Not in your campaign';
  return (
    <div
      className="flex items-center gap-2.5 py-1.5"
      data-testid="waymark-receipt-row"
      data-in-campaign={entry.inCampaign ? 'true' : 'false'}
    >
      <span className="w-14 h-8 rounded-md overflow-hidden shrink-0 bg-black">
        {posterUrl ? (
          <img src={posterUrl} alt="" className="w-full h-full object-cover" />
        ) : videoSrc ? (
          <video
            src={`${videoSrc}#t=0.5`}
            preload="metadata"
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <span
            className="block w-full h-full"
            style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.35), rgba(255,255,255,0.15))' }}
          />
        )}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[12.5px] font-semibold text-slate-900">
          Commercial {entry.ordinal}
        </span>
        <span
          className="block text-[11.5px] text-slate-500"
          data-testid="waymark-receipt-status"
          data-phase={reviewPhase ?? undefined}
        >
          {statusCopy}
        </span>
      </span>
      {onCustomize && (
        <button
          type="button"
          onClick={onCustomize}
          disabled={reviewBusy}
          className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 underline underline-offset-[3px] decoration-slate-300 transition-colors shrink-0 disabled:opacity-40 disabled:cursor-default disabled:hover:text-slate-600"
          data-testid="waymark-receipt-customize"
        >
          Review
        </button>
      )}
      {!entry.inCampaign && onAdd && (
        <button
          type="button"
          onClick={onAdd}
          disabled={adding || reviewBusy}
          className="text-[11px] font-semibold text-white rounded-full px-3 py-1 shrink-0 transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ background: accentColor }}
          data-testid="waymark-receipt-add"
        >
          Add to campaign
        </button>
      )}
    </div>
  );
};

// CTV-785: the side session's failure, under the receipt rows it belongs to.
const ReviewError: React.FC<{ message: string }> = ({ message }) => (
  <p className="text-[11.5px] text-rose-700 py-1" role="alert" data-testid="waymark-review-error">
    {message}
  </p>
);

// The utility strip: everything secondary lives here, on one wrapping line.
// The FAQ list expands below the strip, full width.
const LedgerFooter: React.FC<{
  accentColor: string;
  primaryColor?: string;
  publisherName: string;
  sourceValue: string;
  onSourceChange: (next: string) => void;
  onBeforeSourceCommit?: (next: string) => Promise<boolean>;
  onCustomize?: () => void;
  onStartOver?: () => void;
}> = ({
  accentColor,
  primaryColor,
  publisherName,
  sourceValue,
  onSourceChange,
  onBeforeSourceCommit,
  onCustomize,
  onStartOver,
}) => {
  const [faqOpen, setFaqOpen] = React.useState(false);
  const items: React.ReactNode[] = [];
  if (sourceValue.trim()) {
    items.push(
      <WaymarkSourceWebsiteRow
        accentColor={accentColor}
        primaryColor={primaryColor}
        value={sourceValue}
        onChange={onSourceChange}
        onBeforeCommit={onBeforeSourceCommit}
        bare
      />,
    );
  }
  if (onCustomize) {
    items.push(
      <button
        type="button"
        onClick={onCustomize}
        className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 underline underline-offset-[3px] decoration-slate-300 transition-colors"
        data-testid="waymark-customize-link"
      >
        Review
      </button>,
    );
  }
  if (onStartOver) {
    items.push(
      <button
        type="button"
        onClick={onStartOver}
        className="text-[12px] font-semibold text-slate-500 hover:text-slate-900 underline underline-offset-[3px] decoration-slate-300 transition-colors"
        data-testid="waymark-start-over"
      >
        Start over
      </button>,
    );
  }
  items.push(
    <button
      type="button"
      onClick={() => {
        analytics.track(EVENTS.WAYMARK_FAQ_OPENED, { placement: 'panel' });
        setFaqOpen(true);
      }}
      aria-haspopup="dialog"
      className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-slate-800 transition-colors"
    >
      <HelpCircle className="w-3.5 h-3.5" aria-hidden />
      How does this work?
    </button>,
  );
  return (
    <div className="mt-2.5 pt-2.5 border-t border-slate-100" data-testid="waymark-ledger-footer">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        {items.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <span aria-hidden className="text-slate-300">
                ·
              </span>
            )}
            {item}
          </React.Fragment>
        ))}
      </div>
      {faqOpen && (
        <FaqModal
          accentColor={accentColor}
          publisherName={publisherName}
          onClose={() => setFaqOpen(false)}
        />
      )}
    </div>
  );
};

// CTV-775: the AI commercial FAQ as a MODAL (a dropdown inside the expanded
// shell pushed the whole surface around; the modal keeps the ledger stable).
// Same shell as the restart confirm: overlay + accent rail card.
const FaqModal: React.FC<{
  accentColor: string;
  publisherName: string;
  onClose: () => void;
}> = ({ accentColor, publisherName, onClose }) => (
  <>
    <div
      className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-50"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    />
    <div
      className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-[560px] max-h-[85vh] overflow-y-auto bg-white rounded-2xl border border-slate-200/80"
      style={{ boxShadow: '0 24px 48px -16px rgba(15,23,42,0.28), 0 2px 4px -1px rgba(15,23,42,0.05)' }}
      role="dialog"
      aria-label="How does this work?"
      onClick={(e) => e.stopPropagation()}
      data-testid="waymark-faq-modal"
    >
      <div
        className="h-[3px]"
        style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }}
      />
      <div className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[15px] font-semibold tracking-tight text-slate-900">
            How does this work?
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>
        <div className="mt-3">
          <AdvertiserFaqList
            accentColor={accentColor}
            publisherName={publisherName}
            compact
            faq="ai-commercial"
          />
        </div>
      </div>
    </div>
  </>
);

// CTV-775: the AI commercial FAQ trigger at the bottom of the idle cards
// (the ledger's footer carries its own). Opens the same modal.
const HowDoesThisWork: React.FC<{ accentColor: string; publisherName: string }> = ({
  accentColor,
  publisherName,
}) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-3" data-testid="waymark-how-does-this-work">
      <button
        type="button"
        onClick={() => {
          analytics.track(EVENTS.WAYMARK_FAQ_OPENED, { placement: 'panel' });
          setOpen(true);
        }}
        aria-haspopup="dialog"
        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-800 transition-colors"
      >
        <HelpCircle className="w-3.5 h-3.5" aria-hidden />
        How does this work?
      </button>
      {open && (
        <FaqModal
          accentColor={accentColor}
          publisherName={publisherName}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

// CTV-558: plain confirm modal shown when the user edits the source URL
// during topic_searching / generating / ready. Modeled on the
// CancelCampaignModal shell (overlay + accent rail card + two buttons) but
// inlined and minimal, no type-to-confirm gate since the action is fully
// reversible.
const RestartGenerationConfirm: React.FC<{
  accentColor: string;
  primaryColor?: string;
  // CTV-775: Start over reuses this confirm with its own title, button label,
  // and a body that names the consequence (the current commercial is dropped).
  title?: string;
  confirmLabel?: string;
  body?: 'rebuild' | 'discard';
  newUrl: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({
  accentColor,
  primaryColor,
  title = 'Restart generation?',
  confirmLabel = 'Restart',
  body = 'rebuild',
  newUrl,
  onCancel,
  onConfirm,
}) => (
  <>
    <div
      className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-50"
      onClick={(e) => {
        e.stopPropagation();
        onCancel();
      }}
    />
    <div
      className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-[420px] bg-white rounded-2xl border border-slate-200/80 overflow-hidden"
      style={{ boxShadow: '0 24px 48px -16px rgba(15,23,42,0.28), 0 2px 4px -1px rgba(15,23,42,0.05)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="h-[3px]"
        style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }}
      />
      <div className="p-5">
        <div className="text-[15px] font-semibold tracking-tight text-slate-900">
          {title}
        </div>
        <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed">
          {body === 'discard' ? "We'll set this commercial aside and build a fresh one from" : "We'll rebuild your commercial from"}{' '}
          <span className="font-medium text-slate-800">{newUrl}</span>. This takes about
          5 minutes total.
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-[12px] font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-full transition-colors"
          >
            Cancel
          </button>
          <PrimaryButton
            type="button"
            onClick={onConfirm}
            accentColor={accentColor}
            primaryColor={primaryColor}
            size="sm"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {confirmLabel}
          </PrimaryButton>
        </div>
      </div>
    </div>
  </>
);

// CTV-558: inline website input shown on the Waymark idle panel when the
// buyer hasn't supplied a businessWebsite yet. Lets them seed Waymark
// without bouncing back to Step 1.
const WaymarkWebsiteCapture: React.FC<{
  accentColor: string;
  primaryColor?: string;
  onCommit: (url: string) => void;
}> = ({ accentColor, primaryColor, onCommit }) => {
  const [draft, setDraft] = React.useState('');
  const trimmed = draft.trim();
  // Borderless: WaymarkDisclosure's attached shell is the card.
  return (
    <div>
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: tintFromAccent(accentColor, 0.14), color: accentColor }}
        >
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-1"
            style={{ color: accentColor }}
          >
            Don't have a commercial?
          </div>
          <div className="text-[15px] font-semibold tracking-tight text-slate-900">
            Build one from your website
          </div>
          <p className="text-[12px] text-slate-500 leading-relaxed mt-1">
            Paste your business website and we'll draft a free 30-second commercial you can edit.
          </p>
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && trimmed) {
                  e.preventDefault();
                  onCommit(trimmed);
                }
              }}
              placeholder="yourbusiness.com"
              className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-offset-1"
              style={{ ['--tw-ring-color' as string]: accentColor }}
            />
            <PrimaryButton
              type="button"
              onClick={() => trimmed && onCommit(trimmed)}
              disabled={!trimmed}
              accentColor={accentColor}
              primaryColor={primaryColor}
              size="sm"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Build my commercial
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// CTV-558 / CTV-564: tiny "current: <url>" + edit pencil row used in the
// Waymark idle panel, the failure banner, and (CTV-775) the ledger's utility
// strip. Edits flip to an inline input with explicit Save / Cancel buttons.
// `onBeforeCommit` lets the parent gate the change (e.g., to confirm
// "restart generation?" during topic_searching/generating/ready). The editor
// dismisses after onBeforeCommit resolves either way: true commits the new
// URL, false rolls back to the prior value. `bare` drops the top margin so
// the row can sit inline in the ledger's strip.
const WaymarkSourceWebsiteRow: React.FC<{
  accentColor: string;
  primaryColor?: string;
  value: string;
  onChange: (next: string) => void;
  onBeforeCommit?: (next: string) => Promise<boolean>;
  bare?: boolean;
}> = ({ accentColor, primaryColor, value, onChange, onBeforeCommit, bare = false }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const [error, setError] = React.useState<string | null>(null);
  const committingRef = React.useRef(false);

  React.useEffect(() => {
    if (!editing) {
      setDraft(value);
      setError(null);
    }
  }, [editing, value]);

  const cancel = () => {
    if (committingRef.current) return;
    setEditing(false);
  };

  const save = async () => {
    if (committingRef.current) return;
    const next = draft.trim();
    if (!next || next === value) {
      setEditing(false);
      return;
    }
    // CTV-564: reject obvious garbage before invoking the modal /
    // restarting Waymark. Real-but-dead domains still fall through to the
    // existing topic_failed banner; this only catches non-URL inputs.
    if (!looksLikeWebsiteUrl(next)) {
      setError("That doesn't look like a website URL.");
      return;
    }
    if (onBeforeCommit) {
      committingRef.current = true;
      try {
        // CTV-562 follow-up: onBeforeCommit may throw when a side-effect
        // (like a seat PATCH) fails. Catch here so the user sees the
        // failure inline and stays in edit mode to retry; closing the
        // editor + silently reverting would hide the failure.
        const ok = await onBeforeCommit(next);
        if (ok) {
          onChange(next);
          setEditing(false);
        } else {
          // User cancelled a confirm: close cleanly, no error to show.
          setEditing(false);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save.');
      } finally {
        committingRef.current = false;
      }
      return;
    }
    onChange(next);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className={`${bare ? '' : 'mt-1 '}flex items-center gap-1.5 text-[12px] text-slate-600 min-w-0`}>
        <span className="text-slate-400">Source:</span>
        <span className="font-medium text-slate-700 truncate">{value}</span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Edit website"
          className="inline-flex items-center justify-center w-6 h-6 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className={`${bare ? 'w-full ' : 'mt-1 '}space-y-1`}>
      <div className="flex items-center gap-2">
        <input
          type="text"
          autoFocus
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
          className="flex-1 px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ ['--tw-ring-color' as string]: accentColor }}
        />
        <PrimaryButton
          type="button"
          onClick={() => void save()}
          accentColor={accentColor}
          primaryColor={primaryColor}
          size="sm"
        >
          Save
        </PrimaryButton>
        <button
          type="button"
          onClick={cancel}
          className="text-[12px] font-medium text-slate-500 hover:text-slate-700 px-2 py-1.5 transition-colors"
        >
          Cancel
        </button>
      </div>
      {error && (
        <div className="text-[11px] font-medium text-rose-600">{error}</div>
      )}
    </div>
  );
};

export default WaymarkPanel;
